import React, { useState } from "react";

import axios from "axios";
import { API_BASE } from "../../../config";
import { toast } from "react-hot-toast";

import { motion } from "framer-motion";

import {
  X,
  Send,
  Paperclip,
  MessageSquarePlus,
} from "lucide-react";

const categories = [
  "Bug",
  "Suggestion",
  "Payment",
  "Content",
  "MCQ",
  "Mains",
  "UI",
  "Other",
];

const priorities = [
  "Low",
  "Medium",
  "High",
];

const CreateTicketModal = ({
  onClose,
  refreshTickets,
}) => {

  const [subject, setSubject] =
    useState("");

  const [category, setCategory] =
    useState("Other");

  const [priority, setPriority] =
    useState("Medium");

  const [message, setMessage] =
    useState("");

  const [attachment, setAttachment] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const user =
    JSON.parse(localStorage.getItem("user"));

  const token = user?.token;

  // ====================================
  // CREATE TICKET
  // ====================================
  const handleCreateTicket = async () => {

    if (!subject.trim()) {
      return toast.error("Please enter subject");
    }

    if (!message.trim()) {
      return toast.error("Please enter message");
    }
    

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append(
        "subject",
        subject
      );

      formData.append(
        "category",
        category
      );

      formData.append(
        "priority",
        priority
      );

      formData.append(
        "message",
        message
      );

      if (attachment) {
        formData.append(
          "attachment",
          attachment
        );
      }

      await axios.post(
`${API_BASE}/api/user/feedback/create`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      await refreshTickets();

      onClose();

    } catch (error) {
      console.error(

"Create Ticket Error:",

error.response?.data ||

error.message

);

toast.error(

error.response?.data?.message ||

"Failed to create ticket"

);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="
        fixed
        inset-0
        z-50
        flex
        items-center
        justify-center
        bg-black/60
        backdrop-blur-sm
        p-4
      "
    >

      {/* MODAL */}
      <motion.div
        initial={{
          opacity: 0,
          scale: 0.95,
          y: 20,
        }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0,
        }}
        exit={{
          opacity: 0,
          scale: 0.95,
        }}
        className="
          w-full
          max-w-2xl
          rounded-3xl
          border
          border-white/10
          bg-[#0f1117]
          shadow-2xl
          overflow-hidden
        "
      >

        {/* HEADER */}
        <div
          className="
            px-6
            py-5
            border-b
            border-white/10
            flex
            items-center
            justify-between
          "
        >
          <div className="flex items-center gap-3">

            <div
              className="
                w-12
                h-12
                rounded-2xl
                bg-gradient-to-r
                from-indigo-500
                to-purple-500
                flex
                items-center
                justify-center
              "
            >
              <MessageSquarePlus
                className="text-white"
              />
            </div>

            <div>
              <h2 className="text-xl font-bold text-white">
                Create Support Ticket
              </h2>

              <p className="text-sm text-gray-400">
                Tell us your issue or
                suggestion
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="
              w-10
              h-10
              rounded-xl
              bg-white/[0.05]
              hover:bg-white/[0.08]
              flex
              items-center
              justify-center
              transition
            "
          >
            <X
              size={18}
              className="text-gray-300"
            />
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-5">

          {/* SUBJECT */}
          <div>
            <label
              className="
                block
                text-sm
                font-medium
                text-gray-300
                mb-2
              "
            >
              Subject
            </label>

            <input
              type="text"
              value={subject}
              onChange={(e) =>
                setSubject(e.target.value)
              }
              placeholder="Briefly describe your issue"
              className="
                w-full
                rounded-2xl
                bg-white/[0.05]
                border
                border-white/10
                px-4
                py-3
                text-white
                placeholder-gray-500
                outline-none
                focus:border-indigo-500/50
                transition
              "
            />
          </div>

          {/* CATEGORY + PRIORITY */}
          <div className="grid grid-cols-2 gap-4">

            {/* CATEGORY */}
            <div>
              <label
                className="
                  block
                  text-sm
                  font-medium
                  text-gray-300
                  mb-2
                "
              >
                Category
              </label>

              <select
                value={category}
                onChange={(e) =>
                  setCategory(
                    e.target.value
                  )
                }
                className="
                  w-full
                  rounded-2xl
                  bg-white/[0.05]
                  border
                  border-white/10
                  px-4
                  py-3
                  text-white
                  outline-none
                  focus:border-indigo-500/50
                "
              >
                {categories.map((cat) => (
                  <option
                    key={cat}
                    value={cat}
                    className="bg-[#111827]"
                  >
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* PRIORITY */}
            <div>
              <label
                className="
                  block
                  text-sm
                  font-medium
                  text-gray-300
                  mb-2
                "
              >
                Priority
              </label>

              <select
                value={priority}
                onChange={(e) =>
                  setPriority(
                    e.target.value
                  )
                }
                className="
                  w-full
                  rounded-2xl
                  bg-white/[0.05]
                  border
                  border-white/10
                  px-4
                  py-3
                  text-white
                  outline-none
                  focus:border-indigo-500/50
                "
              >
                {priorities.map((p) => (
                  <option
                    key={p}
                    value={p}
                    className="bg-[#111827]"
                  >
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* MESSAGE */}
          <div>
            <label
              className="
                block
                text-sm
                font-medium
                text-gray-300
                mb-2
              "
            >
              Message
            </label>

            <textarea
              rows={6}
              value={message}
              onChange={(e) =>
                setMessage(e.target.value)
              }
              placeholder="Describe your issue in detail..."
              className="
                w-full
                rounded-2xl
                bg-white/[0.05]
                border
                border-white/10
                px-4
                py-3
                text-white
                placeholder-gray-500
                outline-none
                resize-none
                focus:border-indigo-500/50
                transition
              "
            />
          </div>

          {/* ATTACHMENT */}
          <div>
            <label
              className="
                flex
                items-center
                justify-center
                gap-3
                w-full
                border-2
                border-dashed
                border-white/10
                rounded-2xl
                py-6
                cursor-pointer
                hover:border-indigo-500/40
                hover:bg-indigo-500/5
                transition
              "
            >
              <Paperclip
                size={18}
                className="text-indigo-400"
              />

              <span className="text-gray-300 text-sm">
                {attachment
                  ? attachment.name
                  : "Upload screenshot or document"}
              </span>

              <input
                type="file"
                hidden
                onChange={(e) =>
                  setAttachment(
                    e.target.files[0]
                  )
                }
              />
            </label>
          </div>
        </div>

        {/* FOOTER */}
        <div
          className="
            px-6
            py-5
            border-t
            border-white/10
            flex
            justify-end
            gap-3
          "
        >
          <button
            onClick={onClose}
            className="
              px-5
              py-3
              rounded-2xl
              bg-white/[0.05]
              text-gray-300
              hover:bg-white/[0.08]
              transition
            "
          >
            Cancel
          </button>

          <button
            onClick={handleCreateTicket}
            disabled={loading}
            className="
              px-6
              py-3
              rounded-2xl
              bg-gradient-to-r
              from-indigo-500
              to-purple-500
              text-white
              font-medium
              flex
              items-center
              gap-2
              hover:scale-105
              transition
              disabled:opacity-50
            "
          >
            <Send size={16} />

            {loading
              ? "Creating..."
              : "Create Ticket"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default CreateTicketModal;